// src/context/AuthContext.js
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Fetch full user details from backend
    const fetchUserDetails = async (token) => {
        try {
            const response = await axios.get(`/api/authApplicant/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            // console.error('Failed to fetch user details:', error);
            return null;
        }
    };

    useEffect(() => {
        const initializeUser = async () => {
            const token = localStorage.getItem('token');
            
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                // 1. Decode JWT to get IDs
                const decoded = jwtDecode(token);
                
                // 2. Fetch full user details from backend
                const userDetails = await fetchUserDetails(token, decoded.applicantId);
                
                if (userDetails) {
                    setUser({
                        applicantId: decoded.applicantId,
                        userId: decoded.applicantId,
                        // registerId: decoded.registerId,
                        name: userDetails.name,
                        email: userDetails.email,
                        profilePicture: userDetails.profilePicture
                    });
                } else {
                    // If fetch fails, at least set the IDs from JWT
                    setUser({
                        applicantId: decoded.applicantId,
                        userId: decoded.applicantId,
                        // registerId: decoded.registerId
                    });
                }
                
            } catch (error) {
                console.error('Failed to initialize user:', error);
                localStorage.removeItem('token');
                setUser(null);
            } finally {
                setLoading(false);
            }
        };

        initializeUser();
    }, []);

    const login = useCallback(async (token) => {
        localStorage.setItem('token', token);
        
        try {
            // 1. Decode JWT
            const decoded = jwtDecode(token);
            
            // 2. Fetch full details from backend
            const userDetails = await fetchUserDetails(token, decoded.applicantId);
            
            if (userDetails) {
                setUser({
                    applicantId: decoded.applicantId,
                    userId: decoded.applicantId,
                    // registerId: decoded.registerId,
                    name: userDetails.name,
                    email: userDetails.email,
                    profilePicture: userDetails.profilePicture
                });
            } else {
                setUser({
                    applicantId: decoded.applicantId,
                    userId: decoded.applicantId,
                    // registerId: decoded.registerId
                });
            }
        } catch (error) {
            // console.error('Login failed:', error);
            localStorage.removeItem('token');
            setUser(null);
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        setUser(null);
    }, []);

    const value = {
        user,
        loading,
        
        applicantId: user?.applicantId,
        userId: user?.userId,
        // registerId: user?.registerId,
        
        name: user?.name,
        email: user?.email,
        profilePicture: user?.profilePicture,
        
        // Auth functions
        login,
        logout
    };
    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};